import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Shield, Lock, ArrowLeft, Check, AlertTriangle } from "lucide-react";
import API from "@/lib/api";

export default function ResetPassword() {
  const { branding } = useBranding();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [userInfo, setUserInfo] = useState({ name: "", email: "" });
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const platformName = branding.platform_name || "MEP";

  useEffect(() => {
    if (!token) {
      setValidating(false);
      return;
    }
    API.post("/auth/recovery/validate", { token })
      .then(r => {
        setValid(true);
        setUserInfo({ name: r.data.name, email: r.data.email });
      })
      .catch(() => setValid(false))
      .finally(() => setValidating(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirmPassword) { toast.error("Passwords do not match"); return; }

    setSubmitting(true);
    try {
      await API.post("/auth/recovery/reset", { token, new_password: password });
      setSuccess(true);
      toast.success("Password has been reset successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page min-h-screen flex flex-col items-center justify-center relative overflow-hidden" data-testid="reset-password-page">
      <div className="login-bg absolute inset-0" />
      <div className="login-glow-1 absolute w-[600px] h-[600px] rounded-full" />
      <div className="login-glow-2 absolute w-[400px] h-[400px] rounded-full" />

      <div className="relative z-10 w-full max-w-sm px-6 flex flex-col items-center">
        {/* Logo */}
        <div className="mb-10 text-center">
          <div className="logo-pulse-container mx-auto mb-5">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={platformName} className="h-24 mx-auto rounded-2xl" />
            ) : (
              <img src="/furd-logo-dark.png" alt="FURD Tech" className="h-24 rounded-2xl" />
            )}
          </div>
        </div>

        {validating ? (
          <div className="text-center" data-testid="reset-validating">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-slate-400">Validating recovery token...</p>
          </div>
        ) : !token ? (
          <div className="text-center" data-testid="reset-no-token">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">No Recovery Token</h2>
            <p className="text-sm text-slate-400">This page requires a valid password recovery link.</p>
          </div>
        ) : !valid ? (
          <div className="text-center" data-testid="reset-invalid-token">
            <AlertTriangle className="w-10 h-10 text-rose-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Invalid or Expired Token</h2>
            <p className="text-sm text-slate-400 mb-1">This recovery link is no longer valid.</p>
            <p className="text-xs text-slate-600">Ask your administrator to generate a new recovery link.</p>
          </div>
        ) : success ? (
          <div className="text-center" data-testid="reset-success">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-5">
              <Check className="w-7 h-7 text-emerald-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-3">Password Reset</h2>
            <p className="text-sm text-slate-400 mb-4">Your password has been changed. You can now sign in.</p>
            <Link to="/login">
              <Button className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="reset-go-login">
                Go to Sign In
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-500/10 border border-blue-500/20 mb-4">
                <Shield className="w-5 h-5 text-blue-400" />
              </div>
              <h1 className="text-xl font-bold text-white mb-2" data-testid="reset-title">Set New Password</h1>
              <p className="text-sm text-slate-400">
                {userInfo.name && <span className="text-slate-300 font-medium">{userInfo.name}</span>}
                {userInfo.email && <span className="text-slate-500"> ({userInfo.email})</span>}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="w-full space-y-5" data-testid="reset-form">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-500 uppercase tracking-wider">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                  <Input
                    data-testid="reset-new-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="login-input h-12 pl-11 bg-slate-900/60 border-slate-800/80 text-white placeholder:text-slate-600 focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/50 rounded-lg text-sm"
                    placeholder="Minimum 6 characters"
                    required
                    autoFocus
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                  <Input
                    data-testid="reset-confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="login-input h-12 pl-11 bg-slate-900/60 border-slate-800/80 text-white placeholder:text-slate-600 focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/50 rounded-lg text-sm"
                    placeholder="Re-enter password"
                    required
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold h-12 rounded-lg shadow-lg shadow-blue-500/10 transition-all text-sm tracking-wide"
                data-testid="reset-submit-btn"
              >
                {submitting ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
          </>
        )}

        <div className="mt-8 text-center">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-400 transition-colors" data-testid="back-to-login">
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
